/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form.searchform');
    this.input = this.querySelector('input[type="search"]');
    this.defaultTab = this.querySelector('.side-panel-content--initial');
    this.predictiveSearchResults = this.querySelector('.side-panel-content--has-tabs');
    this.closeButton = this.querySelector('.side-panel-close-button') || this.querySelector('side-panel-close');
    this.handleFocusTrap = this.handleFocusTrap.bind(this);
    this.handleFocusGuard = this.handleFocusGuard.bind(this);
    this.isFocusTrapActive = false;
    this.previouslyFocusedElement = null;
    this.autocompleteContainerSelector = [
      '.fast-simon-autocomplete',
      '#fast-autocomplete-1234',
      '#mobile-optimized-autocomplete',
      '.fast-ac-container',
      '.mobile-fast-ac-container'
    ].join(',');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.addEventListener('toggle', (event) => {
      event.preventDefault();
      const shouldOpen = !this.classList.contains('active');

      document.body.classList.toggle('open-cc', shouldOpen);
      this.classList.toggle('active', shouldOpen);
      this.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');

      if (shouldOpen) {
        this.previouslyFocusedElement = document.activeElement;
        this.enableFocusTrap();
        setTimeout(() => this.focusFirstElement(), 100);
        dispatchCustomEvent('search:open');
      } else {
        this.disableFocusTrap(true);
      }
    });

    this.setAttribute('aria-hidden', this.classList.contains('active') ? 'false' : 'true');
    this.activeStateObserver = new MutationObserver(() => {
      if (this.classList.contains('active')) {
        this.setAttribute('aria-hidden', 'false');
        this.enableFocusTrap();
      } else {
        this.setAttribute('aria-hidden', 'true');
        this.disableFocusTrap(true);
      }
    });
    this.activeStateObserver.observe(this, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  handleFocusTrap(e) {
    if (!this.isFocusTrapActive || e.key !== 'Tab') {
      return;
    }

    const focusableElements = this.getFocusableElements();
    const firstFocusableElement = focusableElements[0] || this;
    const lastFocusableElement = focusableElements[focusableElements.length - 1] || firstFocusableElement;

    if (!this.containsFocusTrapElement(document.activeElement)) {
      e.preventDefault();
      firstFocusableElement.focus({
        preventScroll: true
      });
    } else if (document.activeElement == firstFocusableElement && e.shiftKey) {
      e.preventDefault();
      lastFocusableElement.focus({
        preventScroll: true
      });
    } else if (document.activeElement == lastFocusableElement && !e.shiftKey) {
      e.preventDefault();
      firstFocusableElement.focus({
        preventScroll: true
      });
    }
  }

  handleFocusGuard(event) {
    if (!this.isFocusTrapActive || this.containsFocusTrapElement(event.target)) {
      return;
    }
    this.focusFirstElement();
  }

  getFocusTrapContainers() {
    const containers = [this];

    document.querySelectorAll(this.autocompleteContainerSelector).forEach((container) => {
      const style = window.getComputedStyle(container);
      const hasVisibleBox = container.getClientRects().length > 0 && style.display !== 'none' && style.visibility !== 'hidden';
      const hasVisibleFocusableChildren = Array.from(container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'))
        .some((element) => this.isVisibleFocusableElement(element));

      if (hasVisibleBox || hasVisibleFocusableChildren) {
        containers.push(container);
      }
    });

    return containers;
  }

  containsFocusTrapElement(element) {
    return this.getFocusTrapContainers().some((container) => container.contains(element));
  }

  getFocusableElements() {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])'
    ].join(',');

    return this.getFocusTrapContainers()
      .flatMap((container) => Array.from(container.querySelectorAll(selector)))
      .filter((element, index, elements) => elements.indexOf(element) === index)
      .filter((element) => this.isVisibleFocusableElement(element));
  }

  isVisibleFocusableElement(element) {
    if (element.closest('[hidden], [aria-hidden="true"], [inert]')) {
      return false;
    }
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    return element.getClientRects().length > 0 || element === document.activeElement;
  }

  focusFirstElement() {
    const focusableElements = this.getFocusableElements();
    const firstFocusableElement = focusableElements[0] || this.closeButton || this;

    firstFocusableElement.focus({
      preventScroll: true
    });
  }

  enableFocusTrap() {
    if (this.isFocusTrapActive) {
      return;
    }
    this.isFocusTrapActive = true;
    document.addEventListener('keydown', this.handleFocusTrap);
    document.addEventListener('focusin', this.handleFocusGuard);
  }

  disableFocusTrap(returnFocus = false) {
    if (!this.isFocusTrapActive) {
      return;
    }
    this.isFocusTrapActive = false;
    document.removeEventListener('keydown', this.handleFocusTrap);
    document.removeEventListener('focusin', this.handleFocusGuard);

    if (returnFocus && this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
      setTimeout(() => {
        this.previouslyFocusedElement.focus({
          preventScroll: true
        });
      }, 100);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.predictiveSearchResults.classList.remove('active');
      return;
    }
    this.predictiveSearchResults.classList.add('active');
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    this.predictiveSearchResults.classList.add('loading');

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`)
      .then((response) => {
        this.predictiveSearchResults.classList.remove('loading');
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    let _this = this,
      submitButton = this.querySelector('#search-results-submit');


    submitButton.addEventListener('click', () => {
      _this.form.submit();
    });
  }

  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    this.disableFocusTrap(true);

    async () => {
      document.querySelectorAll(`.predictive-search-drawer`).forEach(async (element) => {
        element.dispatchEvent(new CustomEvent('set-expanded', {
          detail: {
            expanded: false
          }
        }));
      });
    }
  }
}

if (!window.customElements.get('predictive-search-drawer')) {
  window.customElements.define('predictive-search-drawer', PredictiveSearchDrawer);
}
